'use client';

import { c as _c } from "react/compiler-runtime";
import { EmailVerificationStatus } from '@shared/all/domains/user';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import useUser from '@shared/utils/hooks/useUser';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { client, service } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
import PasswordInput from '../UI/PasswordInput';
import VerificationCard from './VerificationCard';
const User = () => {
  const $ = _c(79);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      client,
      service
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    initializeUser,
    logout
  } = useAuth(t0);
  const router = useRouter();
  const [username, setUsername] = useState(currentUser?.username || "");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  let t1;
  if ($[1] !== currentUser.email || $[2] !== currentUser.username) {
    t1 = () => {
      setUsername(currentUser.username);
      setEmail(currentUser.email ?? "");
    };
    $[1] = currentUser.email;
    $[2] = currentUser.username;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== currentUser) {
    t2 = [currentUser];
    $[4] = currentUser;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== currentUser.hasPassword || $[7] !== logout || $[8] !== router) {
    t3 = async (password, setButtonLoading_0) => {
      if (password.length < 8 && currentUser.hasPassword) {
        return toast("Please enter a valid password.", "", "error");
      }
      setButtonLoading_0(true);
      const [error] = await client.users.requestDeletion.mutate({
        password
      });
      if (!error) {
        logout();
        setTimeout(() => {
          router.push("/");
        }, 1000);
      }
      setButtonLoading_0(false);
    };
    $[6] = currentUser.hasPassword;
    $[7] = logout;
    $[8] = router;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const deleteHandler = t3;
  let t4;
  if ($[10] !== currentUser.email || $[11] !== currentUser.username || $[12] !== email || $[13] !== initializeUser || $[14] !== username) {
    t4 = async () => {
      if (!username) {
        return toast("Username cannot be empty", "", "error");
      }
      if (!email) {
        return toast("Email cannot be empty", "", "error");
      }
      if (!email.includes("@") || !email.includes(".")) {
        return toast("Please enter a valid email address", "", "error");
      }
      setButtonLoading(true);
      const [error_0] = await client.users.updateUser.mutate({
        username: username !== currentUser.username ? username : undefined,
        email: email !== currentUser.email ? email : undefined
      });
      if (!error_0) {
        await initializeUser();
      }
      setButtonLoading(false);
    };
    $[10] = currentUser.email;
    $[11] = currentUser.username;
    $[12] = email;
    $[13] = initializeUser;
    $[14] = username;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const updateUserHandler = t4;
  let t5;
  if ($[16] !== initializeUser) {
    t5 = async () => {
      setButtonLoading(true);
      const [error_1, data] = await client.auth.sendVerificationEmail.mutate();
      if (error_1) {
        return setButtonLoading(false);
      }
      setEmailSent(data.emailSent);
      await initializeUser();
      setButtonLoading(false);
      toast(data.message);
    };
    $[16] = initializeUser;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const verifyEmailAddress = t5;
  let t6;
  if ($[18] !== initializeUser) {
    t6 = async () => {
      setButtonLoading(true);
      const [error_2] = await client.users.cancelDeletion.mutate();
      if (!error_2) {
        await initializeUser();
        toast("Account deletion cancelled", "Your account is active again");
      }
      setButtonLoading(false);
    };
    $[18] = initializeUser;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const cancelDeletionHandler = t6;
  let t7;
  let t8;
  let t9;
  if ($[20] !== buttonLoading || $[21] !== cancelDeletionHandler || $[22] !== currentUser.deletionScheduledAt) {
    const deletionScheduledAt = currentUser.deletionScheduledAt ? new Date(currentUser.deletionScheduledAt) : null;
    const deletionPending = !!deletionScheduledAt && deletionScheduledAt > new Date();
    const deletionDaysRemaining = deletionPending ? Math.max(1, Math.ceil((deletionScheduledAt.getTime() - Date.now()) / 86400000)) : 0;
    t7 = "p-6";
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="mb-4"><h2 className="text-2xl font-semibold">Account Settings</h2><p className="text-textSecondary/60">You can manage and change your account's settings from here. To edit your profile like display name and images, visit your profile page.</p></div>;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    t9 = deletionPending && <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 p-4"><h3 className="font-semibold text-red-600 dark:text-red-400">Your account is scheduled for deletion</h3><p className="mt-1 text-sm text-textSecondary/80">Your account and all data will be permanently deleted in{" "}{deletionDaysRemaining}{" "}{deletionDaysRemaining === 1 ? "day" : "days"} (on{" "}{deletionScheduledAt.toLocaleDateString()}). Cancel the deletion to keep your account.</p><div className="mt-3"><Button text="Cancel deletion" onClick={cancelDeletionHandler} disabled={buttonLoading} loading={buttonLoading} /></div></div>;
    $[20] = buttonLoading;
    $[21] = cancelDeletionHandler;
    $[22] = currentUser.deletionScheduledAt;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t10;
  if ($[27] !== username) {
    t10 = <div className="flex items-end space-x-2"><div className="w-1/2"><Input type="text" label="Username" value={username} onChange={setUsername} /></div></div>;
    $[27] = username;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = currentUser.oauthProviders.length > 0;
  let t12;
  if ($[29] !== email || $[30] !== t11) {
    t12 = <div className="w-1/2"><Input type="email" label="Email" value={email} onChange={setEmail} disabled={t11} /></div>;
    $[29] = email;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== currentUser.oauthProviders.length) {
    t13 = currentUser.oauthProviders.length > 0 && <div className="mb-1 text-sm text-textSecondary/60">Email cannot be changed for OAuth accounts</div>;
    $[32] = currentUser.oauthProviders.length;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13) {
    t14 = <div className="flex items-end space-x-2">{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== buttonLoading || $[38] !== currentUser.email || $[39] !== currentUser.oauthProviders.length || $[40] !== currentUser.username || $[41] !== email || $[42] !== updateUserHandler || $[43] !== username) {
    t15 = (username && currentUser.username !== username || email && currentUser.email !== email && currentUser.oauthProviders.length === 0) && <Button text="Update Account" onClick={updateUserHandler} disabled={!username || !email || buttonLoading} loading={buttonLoading} />;
    $[37] = buttonLoading;
    $[38] = currentUser.email;
    $[39] = currentUser.oauthProviders.length;
    $[40] = currentUser.username;
    $[41] = email;
    $[42] = updateUserHandler;
    $[43] = username;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t10 || $[46] !== t14 || $[47] !== t15) {
    t16 = <div className="space-y-4">{t10}{t14}{t15}</div>;
    $[45] = t10;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== emailSent) {
    t17 = !emailSent && <div className="italic text-textSecondary/60">Verification Failed!</div>;
    $[49] = emailSent;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== currentUser.emailVerificationStatus) {
    t18 = currentUser.emailVerificationStatus === EmailVerificationStatus.PENDING && <div className="italic text-textSecondary/60">Verification pending!</div>;
    $[51] = currentUser.emailVerificationStatus;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== currentUser.emailVerificationStatus || $[54] !== emailSent || $[55] !== verifyEmailAddress) {
    t19 = currentUser.emailVerificationStatus === EmailVerificationStatus.UNVERIFIED || currentUser.emailVerificationStatus === EmailVerificationStatus.PENDING || !emailSent ? <Button text={!emailSent || currentUser.emailVerificationStatus === EmailVerificationStatus.PENDING ? "Resend" : "Verify Email"} classNames="w-28 self-end" onClick={verifyEmailAddress} /> : <div className="italic text-textSecondary/60">Verified!</div>;
    $[53] = currentUser.emailVerificationStatus;
    $[54] = emailSent;
    $[55] = verifyEmailAddress;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t17 || $[58] !== t18 || $[59] !== t19) {
    t20 = <div className="flex flex-col space-y-4"><div className="flex items-center justify-between">{t17}{t18}{t19}</div></div>;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t16 || $[62] !== t20) {
    t21 = <div className="space-y-4">{t16}{t20}</div>;
    $[61] = t16;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="mt-8"><VerificationCard /></div>;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  let t24;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <h3 className="text-xl font-semibold">Delete your account</h3>;
    t24 = <p className="text-textSecondary/60">Your account and all associated data will be permanently deleted within 30 days. This action cannot be undone.</p>;
    $[65] = t23;
    $[66] = t24;
  } else {
    t23 = $[65];
    t24 = $[66];
  }
  let t25;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="mt-8 space-y-4">{t23}{t24}<Button text="Delete Account" variant="DANGER" classNames="text-white rounded" onClick={() => setDeleteUserModal(true)} /></div>;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t21 || $[69] !== t7 || $[70] !== t8 || $[71] !== t9) {
    t26 = <div className={t7}>{t8}{t9}{t21}{t22}{t25}</div>;
    $[68] = t21;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== deleteHandler || $[74] !== deleteUserModal) {
    t27 = deleteUserModal && <DeleteUserModal closeHandler={() => setDeleteUserModal(false)} deleteHandler={deleteHandler} />;
    $[73] = deleteHandler;
    $[74] = deleteUserModal;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== t26 || $[77] !== t27) {
    t28 = <>{t26}{t27}</>;
    $[76] = t26;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  return t28;
};
function DeleteUserModal(t0) {
  const $ = _c(20);
  const {
    closeHandler,
    deleteHandler
  } = t0;
  const [password, setPassword] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2>Delete Account</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== closeHandler) {
    t2 = <ModalHeader closeHandler={closeHandler}>{t1}</ModalHeader>;
    $[1] = closeHandler;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== currentUser.hasPassword || $[4] !== password) {
    t3 = currentUser.hasPassword && <><p className="text-start text-textSecondary/60">If you wish to continue, then please enter your password:</p><PasswordInput value={password} onChange={setPassword} /></>;
    $[3] = currentUser.hasPassword;
    $[4] = password;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mt-2 text-start text-sm text-textSecondary/60">Your account and all associated data will be permanently deleted within 30 days. This action cannot be undone.</p>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <ModalContent>{t3}{t4}</ModalContent>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== deleteHandler || $[10] !== password) {
    t6 = () => deleteHandler(password, setButtonLoading);
    $[9] = deleteHandler;
    $[10] = password;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== buttonLoading || $[13] !== t6) {
    t7 = <ModalFooter><Button text="Delete Account" variant="DANGER" classNames="text-white rounded disabled:opacity-50 self-end" onClick={t6} loading={buttonLoading} disabled={buttonLoading} /></ModalFooter>;
    $[12] = buttonLoading;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== closeHandler || $[16] !== t2 || $[17] !== t5 || $[18] !== t7) {
    t8 = <Modal closeHandler={closeHandler}>{t2}{t5}{t7}</Modal>;
    $[15] = closeHandler;
    $[16] = t2;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
export default User;